import React, { useMemo, useEffect, useCallback } from "react";
import {
  ItineraryItem,
  Location,
  type ActivityItem as ActivityItemType,
  BookingStatus,
} from "@/types";
import useTripStore from "@/stores/tripBuilder/useTrip";
import { useExperiences } from "@/hooks/useExperiences";
import useCurrencyStore from "@/stores/ui/currencyStore";
import { isSameDay } from "date-fns";
import ActivityItem from "@tripbuilder/cat-schedule/ActivityItem";
import logger from "@/utils/logger";

interface ActivityContainerProps {
  selectedLocation: Location;
  selectedDay: string;
  activities: ActivityItemType[];
  onDelete: (id: string, name: string) => void;
}

const ActivityContainer: React.FC<ActivityContainerProps> = ({
  selectedLocation,
  activities,
  onDelete,
}) => {
  const {
    tripData,
    updateTripData,
    selectedDay,
    updateBookingStats,
    checkIsAdmin,
  } = useTripStore();
  const { selectedCurrency } = useCurrencyStore();
  const { handleOpenModal, isFetching, selectedExperienceId } =
    useExperiences(selectedLocation);
  const isAdmin = useMemo(() => checkIsAdmin(), [checkIsAdmin]);

  const calculateBookingStats = useCallback(() => {
    if (!selectedLocation || !tripData)
      return {
        transport: { completed: false },
        accommodation: { completed: false },
        activities: { completed: 0, total: 0 },
      };

    // Get all activities across all days
    const allActivities: ItineraryItem[] = [];
    selectedLocation.itinerary_items.forEach((dayItem) => {
      dayItem.items.forEach((item) => {
        if (!("bookingInfo" in item)) {
          allActivities.push(item);
        }
      });
    });

    // Count unique activities using a Map to handle duplicates
    const uniqueActivities = new Map();
    allActivities.forEach((activity) => {
      uniqueActivities.set(activity.id, activity);
    });

    const stats = {
      transport: { completed: false },
      accommodation: { completed: false },
      activities: {
        total: uniqueActivities.size,
        completed: Array.from(uniqueActivities.values()).filter(
          (activity) => activity.bookingStatus === "confirmed"
        ).length,
      },
    };

    // Check transport and accommodation
    stats.transport.completed =
      tripData.transport?.some(
        (transport) =>
          transport.fromLocationId === selectedLocation.id ||
          transport.toLocationId === selectedLocation.id
      ) || false;

    stats.accommodation.completed =
      selectedLocation.accommodation?.main?.bookingStatus ===
        ("confirmed" as BookingStatus) || false;

    logger.log("Calculated stats:", stats); // Debug log
    return stats;
  }, [tripData, selectedLocation]);

  useEffect(() => {
    if (selectedLocation) {
      const stats = calculateBookingStats();
      updateBookingStats(selectedLocation.id, stats);
    }
  }, [
    selectedLocation?.id,
    tripData,
    calculateBookingStats,
    updateBookingStats,
    selectedLocation,
  ]);

  const handleTimeChange = useCallback(
    (newTime: Date, activityId: string) => {
      logger.log("HANDLE TIME CHANGE", newTime, activityId);
      if (selectedLocation && selectedDay && tripData) {
        const updatedLocations = tripData.locations.map((loc) => {
          if (loc.id === selectedLocation?.id) {
            const updatedItineraryItems = loc.itinerary_items.map((item) => {
              if (
                item.date &&
                isSameDay(new Date(item.date), new Date(selectedDay))
              ) {
                logger.log("UPDATING ITEM", item);
                return {
                  ...item,
                  items: item.items.map((activity: any) =>
                    activity.id === activityId
                      ? { ...activity, start_time: newTime.toISOString() }
                      : activity
                  ),
                };
              }
              return item;
            });
            return { ...loc, itinerary_items: updatedItineraryItems };
          }
          return loc;
        });

        updateTripData({ ...tripData, locations: updatedLocations });
      }
    },
    [selectedDay, tripData, updateTripData, selectedLocation]
  );

  if (!tripData || !selectedLocation || !selectedDay) return null;

  if (activities.length === 0) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center space-y-2 bg-neutral-100 rounded-lg">
        <h1 className="text-xl text-neutral-500 opacity-75">
          No activities scheduled
        </h1>
        <p className="text-sm text-neutral-500 opacity-75 font-light">
          Add activities to your day to get started
        </p>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col items-start justify-start">
      {activities.map((activity, index) => (
        <ActivityItem
          key={activity.name + index.toString()}
          tripId={tripData.id}
          activity={activity}
          nextActivity={activities[index + 1] || null}
          onDelete={onDelete}
          handleTimeChange={handleTimeChange}
          activities={activities}
          locationName={selectedLocation?.name || ""}
          locationTimeZone={selectedLocation?.date.time_zone || ""}
          handleOpenModal={handleOpenModal}
          selectedCurrency={selectedCurrency}
          isAdmin={isAdmin}
        />
      ))}
    </div>
  );
};

export default ActivityContainer;
